import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, CircleCheck, Copy, KeyRound, Laptop, Link2Off, Loader2, Plus, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AgentAvatar } from "@/components/agent-avatar";
import { suggestHandle } from "@/lib/handles";
import { PresenceDot } from "@/components/foreign-presence";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input, Textarea } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { shortPath } from "@/components/project-select";
import { useBoardsQuery } from "@/hooks/use-boards";
import { api, ApiError } from "@/lib/api";
import {
  countdown,
  machineName,
  pairingCommands,
  pairingStage,
  presenceLabel,
  type PairingStage,
} from "@/lib/foreign-agents";
import { qk } from "@/lib/query/keys";
import type { AgentWithContext, BoardDetail } from "@/lib/types";
import { cn } from "@/lib/utils";

/*
 * Foreign agents: Claude Code on someone's own machine, shown on the board as an
 * agent and reached through the `board-link` CLI, which holds a socket to the board.
 * Made here with a pairing code; `board-link pair <code>` on the machine trades it for
 * a token, and `board-link run` connects. Everything that drives one is admin work.
 */

const EVERY_BOARD = "__every__";
const selectAgents = (data: { agents: AgentWithContext[] }) => data.agents;
const NO_AGENTS: AgentWithContext[] = [];
const selectLiveBoards = (data: { boards: BoardDetail[] }) => data.boards.filter((detail) => !detail.board.archived);
const NO_BOARDS: BoardDetail[] = [];

/** Every agent from the shared cache; re-read every few seconds while `watching` (a pairing screen is open). */
function useAgentList(watching = false): AgentWithContext[] {
  return (
    useQuery({ queryKey: qk.agents(), queryFn: api.listAgents, select: selectAgents, refetchInterval: watching ? 3_000 : false })
      .data ?? NO_AGENTS
  );
}

/** A clock that ticks while `on` (every second by default), for the countdown and "last seen". */
function useTick(on: boolean, everyMs = 1_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    setNow(Date.now());
    const handle = setInterval(() => setNow(Date.now()), everyMs);
    return () => clearInterval(handle);
  }, [on, everyMs]);
  return now;
}

/** The board's own address, for the install and pair commands. */
const boardOrigin = () => (typeof window === "undefined" ? "https://board.example.com" : window.location.origin);

function CopyButton({ text, label = "Copy", className }: { text: string; label?: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      type="button"
      variant="outline"
      size="xs"
      className={className}
      aria-label={`${label}: ${text}`}
      onClick={() => {
        void navigator.clipboard.writeText(text).then(
          () => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          },
          () => toast.error("Could not copy — select the text instead"),
        );
      }}
    >
      {copied ? <Check /> : <Copy />} {copied ? "Copied" : label}
    </Button>
  );
}

export interface PairingCode {
  code: string;
  expiresAt: string;
  /** When this screen got the code: a link that connects after it is the one that paired. */
  issuedAt: string;
}

/**
 * The pairing screen: the code, how long it lasts, and the three commands to run on
 * the machine. It follows the agent as the link moves on — waiting, paired, online —
 * from the agents query, which the live feed refreshes on every `agent_links` write
 * (and this screen re-reads every few seconds as well).
 */
export function PairingPanel({
  agent,
  pairing,
  issuing,
  error,
  onNewCode,
}: {
  /** The agent as the list has it now; null while it is being made. */
  agent: AgentWithContext | null;
  pairing: PairingCode | null;
  issuing: boolean;
  error: string | null;
  onNewCode: () => void;
}) {
  const now = useTick(pairing !== null);
  const reading = pairing ? pairingStage(agent?.link, pairing.expiresAt, pairing.issuedAt, now) : "waiting";
  // Once paired it stays paired on this screen, even when the code's own time runs out.
  const [reached, setReached] = useState<PairingStage | null>(null);
  useEffect(() => setReached(null), [pairing?.code]);
  useEffect(() => {
    if (reading === "online" || (reading === "paired" && reached !== "online")) setReached(reading);
  }, [reading, reached]);
  const stage: PairingStage = reached ?? reading;
  const left = pairing ? countdown(pairing.expiresAt, now) : null;
  const commands = pairing ? pairingCommands(boardOrigin(), pairing.code) : [];

  if (!pairing) {
    return (
      <div className="grid place-items-center py-8 text-[13px] text-muted-foreground">
        {error ? (
          <div className="space-y-3 text-center">
            <p className="text-destructive">{error}</p>
            <Button size="sm" variant="outline" onClick={onNewCode} loading={issuing}>
              <RefreshCw /> Try again
            </Button>
          </div>
        ) : (
          <span className="inline-flex items-center gap-2">
            <Loader2 className="size-4 animate-spin" /> Making a pairing code…
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <StageLine stage={stage} agent={agent} />

      {stage === "waiting" || stage === "expired" ? (
        <div className="rounded-lg border border-border/70 bg-surface/60 p-3 text-center">
          <p className="text-[11.5px] font-medium uppercase tracking-wide text-muted-foreground">Pairing code</p>
          <p
            className={cn(
              "my-1.5 select-all font-mono text-3xl font-semibold tracking-[0.18em] tabular-nums",
              stage === "expired" && "text-muted-foreground line-through",
            )}
          >
            {pairing.code}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-2 text-[12px]">
            {stage === "expired" ? (
              <>
                <span className="text-destructive">Expired</span>
                <span className="text-muted-foreground">—</span>
                <Button size="xs" onClick={onNewCode} loading={issuing}>
                  <RefreshCw /> New code
                </Button>
              </>
            ) : (
              <>
                <span className="tabular-nums text-muted-foreground">Expires in {left}</span>
                <CopyButton text={pairing.code} />
                <Button size="xs" variant="ghost" onClick={onNewCode} loading={issuing}>
                  <RefreshCw /> New code
                </Button>
              </>
            )}
          </div>
          {error ? <p className="mt-2 text-[12px] text-destructive">{error}</p> : null}
        </div>
      ) : null}

      <div className="space-y-1.5">
        <p className="text-[12.5px] font-medium">On the machine, run:</p>
        <ol className="space-y-1.5">
          {commands.map((command, index) => (
            <li key={index} className="flex items-center gap-2">
              <span className="w-4 shrink-0 text-right text-[11px] tabular-nums text-muted-foreground">{index + 1}.</span>
              <code className="min-w-0 flex-1 overflow-x-auto whitespace-nowrap rounded-md border border-border/60 bg-surface/60 px-2 py-1.5 font-mono text-[11.5px] scrollbar-slim">
                {command}
              </code>
              <CopyButton text={command} className="shrink-0" />
            </li>
          ))}
        </ol>
        <p className="text-[11.5px] text-muted-foreground">
          The last one names the directory it works in; run it in the project you want the agent in.
        </p>
      </div>

      <p className="text-[12px] text-muted-foreground">
        Your machine's own Claude login runs the agent; only its board tool calls and run output reach the board.
      </p>
    </div>
  );
}

function StageLine({ stage, agent }: { stage: PairingStage; agent: AgentWithContext | null }) {
  const machine = machineName(agent?.link);
  if (stage === "online") {
    return (
      <p className="flex items-center gap-2 rounded-md border border-kind-done/40 bg-kind-done/10 px-3 py-2 text-[13px] font-medium">
        <CircleCheck className="size-4 shrink-0 text-kind-done" />
        Paired with {machine} · Online ✓
        {agent?.link?.dir ? <span className="truncate font-mono text-[11px] font-normal text-muted-foreground">{shortPath(agent.link.dir)}</span> : null}
      </p>
    );
  }
  if (stage === "paired") {
    return (
      <p className="flex items-center gap-2 rounded-md border border-border/70 bg-surface/60 px-3 py-2 text-[13px]">
        <Check className="size-4 shrink-0 text-kind-done" />
        <span>
          <span className="font-medium">Paired with {machine}.</span>{" "}
          <span className="text-muted-foreground">Waiting for <code className="font-mono text-[12px]">board-link run</code> to connect…</span>
        </span>
      </p>
    );
  }
  if (stage === "expired") return null;
  return (
    <p className="flex items-center gap-2 text-[12.5px] text-muted-foreground">
      <Loader2 className="size-3.5 shrink-0 animate-spin" />
      Waiting for <code className="font-mono text-[12px]">board-link pair</code> on your machine…
    </p>
  );
}

/** Issues a fresh code and keeps it, for a pairing screen opened on an agent that exists. */
function usePairingCode(agentId: string | null, open: boolean) {
  const client = useQueryClient();
  const [pairing, setPairing] = useState<PairingCode | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const issue = async () => {
    if (!agentId) return;
    setIssuing(true);
    setError(null);
    try {
      const fresh = await api.newPairingCode(agentId);
      setPairing({ ...fresh, issuedAt: new Date().toISOString() });
      void client.invalidateQueries({ queryKey: qk.agents() });
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not make a pairing code");
    } finally {
      setIssuing(false);
    }
  };
  useEffect(() => {
    if (!open) return;
    setPairing(null);
    void issue();
    // A new code each time the screen opens; the last one is replaced by it anyway.
  }, [open, agentId]); // eslint-disable-line react-hooks/exhaustive-deps
  return { pairing, issuing, error, issue };
}

/** "Pair again…": the pairing screen for an existing foreign agent, with a fresh code. */
export function PairingDialog({
  agent,
  open,
  onOpenChange,
}: {
  agent: AgentWithContext;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const agents = useAgentList(open);
  const live = agents.find((entry) => entry.id === agent.id) ?? agent;
  const { pairing, issuing, error, issue } = usePairingCode(agent.id, open);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Pair {agent.name} with a machine</DialogTitle>
          <DialogDescription>
            {live.link?.paired
              ? `Paired now with ${machineName(live.link)}. Pairing with this code moves it: the old link stops working.`
              : "Run these on the machine it should work on."}
          </DialogDescription>
        </DialogHeader>
        <PairingPanel agent={live} pairing={pairing} issuing={issuing} error={error} onNewCode={() => void issue()} />
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** The board-scope picker: every board, or one. A scope already on an archived board still shows. */
function BoardScopeSelect({
  value,
  onChange,
  boards,
  disabled,
  className,
}: {
  value: string | null;
  onChange: (boardId: string | null) => void;
  boards: BoardDetail[];
  disabled?: boolean;
  className?: string;
}) {
  const known = value === null || boards.some((detail) => detail.board.id === value);
  return (
    <Select value={value ?? EVERY_BOARD} onValueChange={(next) => onChange(next === EVERY_BOARD ? null : next)} disabled={disabled}>
      <SelectTrigger aria-label="Board scope" className={className}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={EVERY_BOARD}>Every board</SelectItem>
        {boards.map((detail) => (
          <SelectItem key={detail.board.id} value={detail.board.id}>
            {detail.board.name}
          </SelectItem>
        ))}
        {!known && value ? <SelectItem value={value}>A board no longer listed</SelectItem> : null}
      </SelectContent>
    </Select>
  );
}

/**
 * "Add foreign agent": a name, a handle, an optional board it is confined to and
 * standing instructions; then the pairing screen for the code the board made.
 */
export function ForeignAgentDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (agent: AgentWithContext) => void;
}) {
  const boards = useBoardsQuery();
  const liveBoards = useMemo(() => (boards.data ? selectLiveBoards(boards.data) : NO_BOARDS), [boards.data]);
  const [name, setName] = useState("");
  const [handle, setHandle] = useState("");
  const [boardId, setBoardId] = useState<string | null>(null);
  const [instructions, setInstructions] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<AgentWithContext | null>(null);
  const [first, setFirst] = useState<PairingCode | null>(null);
  const agents = useAgentList(open && created !== null);
  const live = created ? (agents.find((agent) => agent.id === created.id) ?? created) : null;
  // A second code for the same agent, if the first ran out.
  const again = usePairingCode(created?.id ?? null, false);
  const pairing = again.pairing ?? first;

  useEffect(() => {
    if (!open) return;
    setName("");
    setHandle("");
    setBoardId(null);
    setInstructions("");
    setError(null);
    setCreated(null);
    setFirst(null);
  }, [open]);

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.createForeignAgent({
        name: name.trim(),
        handle: handle.trim() || undefined,
        boardId,
        instructions: instructions.trim() || null,
      });
      setCreated(result.agent);
      setFirst({ code: result.code, expiresAt: result.expiresAt, issuedAt: new Date().toISOString() });
      onCreated?.(result.agent);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : "Could not add the agent");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>{created ? `Pair ${created.name}` : "Add foreign agent"}</DialogTitle>
          <DialogDescription>
            {created
              ? "Run these on the machine it should work on. This code works once, for ten minutes."
              : "Claude Code on your own machine, on the board as an agent: assign it cards, @mention it, talk to it in its chat. It connects through board-link and runs with that machine's Claude login."}
          </DialogDescription>
        </DialogHeader>

        {created ? (
          <PairingPanel
            agent={live}
            pairing={pairing}
            issuing={again.issuing}
            error={again.error}
            onNewCode={() => void again.issue()}
          />
        ) : (
          <form
            className="space-y-3"
            onSubmit={(event) => {
              event.preventDefault();
              if (name.trim()) void submit();
            }}
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="foreign-name">Name</Label>
                <Input id="foreign-name" value={name} placeholder="Laptop" autoFocus onChange={(event) => setName(event.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="foreign-handle">Handle</Label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">@</span>
                  <Input
                    id="foreign-handle"
                    className="pl-6"
                    value={handle}
                    placeholder={suggestHandle(name) || "laptop"}
                    onChange={(event) => setHandle(event.target.value.replace(/^@/, "").toLowerCase())}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Boards it may use</Label>
              <BoardScopeSelect value={boardId} onChange={setBoardId} boards={liveBoards} />
              <p className="text-[11.5px] text-muted-foreground">
                Its board tools are confined to that one board; every board otherwise. Changeable later in Settings.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="foreign-instructions">Standing instructions</Label>
              <Textarea
                id="foreign-instructions"
                rows={3}
                value={instructions}
                placeholder="You work in my local checkout. Run the tests before you say you are done."
                onChange={(event) => setInstructions(event.target.value)}
              />
            </div>
            {error ? <p className="text-xs text-destructive">{error}</p> : null}
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" loading={saving} disabled={!name.trim()}>
                <KeyRound /> Add and get a code
              </Button>
            </DialogFooter>
          </form>
        )}

        {created ? (
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Done
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** A yes/no that names what it does, for revoking a link and deleting an agent. */
function ConfirmDialog({
  open,
  title,
  body,
  action,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  body: string;
  action: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="destructive" loading={busy} onClick={onConfirm}>
            {action}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

type Confirming = { kind: "revoke" | "delete"; agent: AgentWithContext };

/**
 * Settings → Foreign agents (admins): every foreign agent, where it runs, whether it is
 * connected and which boards it may use, with a fresh code, revoking its link and
 * deleting it. Revoking keeps the agent (and its chat), unpaired, until paired again.
 */
export function ForeignAgentsSettings() {
  const client = useQueryClient();
  const all = useAgentList();
  const agents = useMemo(() => all.filter((agent) => agent.kind === "foreign"), [all]);
  const boardsQuery = useBoardsQuery();
  const liveBoards = useMemo(() => (boardsQuery.data ? selectLiveBoards(boardsQuery.data) : NO_BOARDS), [boardsQuery.data]);
  // "last seen 5m ago" moves by the minute; no need to redraw the list every second.
  const now = useTick(true, 30_000);
  const [adding, setAdding] = useState(false);
  const [pairFor, setPairFor] = useState<AgentWithContext | null>(null);
  const [pairOpen, setPairOpen] = useState(false);
  const [confirming, setConfirming] = useState<Confirming | null>(null);
  const [busy, setBusy] = useState(false);
  const refresh = () => void client.invalidateQueries({ queryKey: qk.agents() });

  const setScope = async (agent: AgentWithContext, boardId: string | null) => {
    try {
      await api.setAgentLinkBoard(agent.id, boardId);
      refresh();
      toast.success(boardId ? `${agent.name} now uses one board` : `${agent.name} may use every board`);
    } catch (cause) {
      toast.error(cause instanceof ApiError ? cause.message : "Could not change its boards");
    }
  };

  const confirm = async () => {
    if (!confirming) return;
    const { kind, agent } = confirming;
    setBusy(true);
    try {
      if (kind === "revoke") await api.revokeAgentLink(agent.id);
      else await api.deleteAgent(agent.id);
      setConfirming(null);
      refresh();
      toast.success(kind === "revoke" ? `${agent.name}'s link revoked` : `${agent.name} deleted`);
    } catch (cause) {
      toast.error(cause instanceof ApiError ? cause.message : `Could not ${kind === "revoke" ? "revoke the link" : "delete the agent"}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      {agents.length === 0 ? (
        <p className="text-[13px] text-muted-foreground">
          None yet. A foreign agent is Claude Code on your own machine, on the board as an agent, connected through{" "}
          <code className="rounded bg-muted px-1">board-link</code>.
        </p>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border">
          {agents.map((agent) => (
            <li key={agent.id} className="flex flex-col gap-2.5 px-3 py-2.5 win-sm:flex-row win-sm:items-center">
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <AgentAvatar agent={agent} />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <p className="flex min-w-0 items-baseline gap-1.5 text-[13.5px] font-medium">
                    <span className="truncate">{agent.name}</span>
                    <span className="truncate font-normal text-muted-foreground">@{agent.handle}</span>
                  </p>
                  <p className="flex min-w-0 items-center gap-1.5 text-[12px] text-muted-foreground">
                    <PresenceDot agent={agent} />
                    <span className="truncate">
                      {agent.link?.paired
                        ? `${machineName(agent.link)}${agent.link.platform ? ` (${agent.link.platform})` : ""} · ${presenceLabel(agent.link, now)}`
                        : agent.link?.pairingExpiresAt
                          ? "not paired yet · a code is waiting"
                          : "not paired yet"}
                    </span>
                  </p>
                  {agent.link?.dir ? (
                    <p className="flex min-w-0 items-center gap-1.5 text-[11.5px] text-muted-foreground">
                      <Laptop className="size-3 shrink-0" />
                      <span className="truncate font-mono">{shortPath(agent.link.dir)}</span>
                    </p>
                  ) : null}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5 win-sm:justify-end">
                <BoardScopeSelect
                  value={agent.link?.boardId ?? null}
                  onChange={(boardId) => void setScope(agent, boardId)}
                  boards={liveBoards}
                  className="h-7 w-40 text-[12px]"
                />
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => {
                    setPairFor(agent);
                    setPairOpen(true);
                  }}
                >
                  <KeyRound /> New pairing code
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  disabled={!agent.link?.paired}
                  onClick={() => setConfirming({ kind: "revoke", agent })}
                >
                  <Link2Off /> Revoke link
                </Button>
                <Button
                  size="xs"
                  variant="ghost"
                  className="text-destructive"
                  aria-label={`Delete ${agent.name}`}
                  onClick={() => setConfirming({ kind: "delete", agent })}
                >
                  <Trash2 /> Delete
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Button size="sm" variant="outline" onClick={() => setAdding(true)}>
        <Plus /> Add foreign agent
      </Button>

      <ForeignAgentDialog open={adding} onOpenChange={setAdding} onCreated={refresh} />
      {pairFor ? <PairingDialog agent={pairFor} open={pairOpen} onOpenChange={setPairOpen} /> : null}
      <ConfirmDialog
        open={confirming !== null}
        title={
          confirming?.kind === "revoke"
            ? `Revoke ${confirming.agent.name}'s link to ${machineName(confirming.agent.link)}?`
            : `Delete ${confirming?.agent.name ?? "the agent"}?`
        }
        body={
          confirming?.kind === "revoke"
            ? `${machineName(confirming.agent.link)} is disconnected at once and its token stops working. The agent and its chat stay; pair it again with a new code.`
            : `Its chat goes with it, and ${confirming?.agent.link?.paired ? `${machineName(confirming.agent.link)} is disconnected` : "any pairing code stops working"}. Cards and comments that name it keep its name.`
        }
        action={confirming?.kind === "revoke" ? "Revoke link" : "Delete agent"}
        busy={busy}
        onCancel={() => setConfirming(null)}
        onConfirm={() => void confirm()}
      />
    </div>
  );
}
